import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAudioPlayer } from "@/context/AudioContext";
import { radioStations, type RadioStation } from "@/mocks/radios";
import { radioProfiles, type RadioProfile } from "@/mocks/radioProfile";
import { programs, type Program } from "@/mocks/programs";
import { clips, duracionLabels as clipDuracionLabels, formatearNumero, type Clip } from "@/mocks/clips";
import { adminEvents, type RadioEvent } from "@/mocks/adminEvents";
import { adminContests, type Contest } from "@/mocks/adminContests";
import TvCarousel from "@/pages/tv-app/components/TvCarousel";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function TvRadioProfilePage() {
  const navigate = useNavigate();
  const { radioId } = useParams<{ radioId: string }>();
  const { currentStation, isPlaying, play, toggle } = useAudioPlayer();
  const [focusedBtn, setFocusedBtn] = useState<string>("back");
  const [station, setStation] = useState<RadioStation | null>(null);
  const [profile, setProfile] = useState<RadioProfile | null>(null);
  const [stationPrograms, setStationPrograms] = useState<Program[]>([]);
  const [stationClips, setStationClips] = useState<Clip[]>([]);
  const [stationEvents, setStationEvents] = useState<RadioEvent[]>([]);
  const [stationContests, setStationContests] = useState<Contest[]>([]);
  const [showQr, setShowQr] = useState(false);

  const scheduleRef = useRef<HTMLDivElement>(null);
  const videosRef = useRef<HTMLDivElement>(null);
  const eventsRef = useRef<HTMLDivElement>(null);
  const contestsRef = useRef<HTMLDivElement>(null);

  const buttons = ["back", "play", "schedule", "videos", "events", "contests"] as const;

  useEffect(() => {
    if (!radioId) return;
    const numId = Number(radioId);
    if (isNaN(numId)) return;
    const s = radioStations.find((r) => r.id === numId);
    if (!s) return;
    setStation(s);
    setProfile(radioProfiles[numId] || null);
    setStationPrograms(programs.filter((p) => p.radioId === numId).slice(0, 6));
    setStationClips(clips.filter((c) => c.radioFrecuencia.includes(s.frequency.replace(" FM", "").replace(" RFW", "")) || c.radioNombre === s.name).slice(0, 6));
    setStationEvents(adminEvents.filter((e) => e.stationId === numId).slice(0, 6));
    setStationContests(adminContests.filter((c) => c.stationId === numId).slice(0, 6));
  }, [radioId]);

  const isCurrentStation = currentStation?.id === station?.id;

  const schedule = useMemo(() => {
    if (!profile) return [];
    const days = ["Lunes - Viernes", "Sábados", "Domingos"];
    return profile.schedule.filter((s) => days.includes(s.day)).slice(0, 7);
  }, [profile]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx + 1) % buttons.length]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (showQr) { setShowQr(false); return; }
        navigate("/tv-app");
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "back") navigate("/tv-app");
        else if (focusedBtn === "play") {
          if (station) {
            if (isCurrentStation) toggle();
            else play(station);
          }
        }
        else if (focusedBtn === "schedule") scheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        else if (focusedBtn === "videos") videosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        else if (focusedBtn === "events") eventsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        else if (focusedBtn === "contests") contestsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (e.key === " ") {
        e.preventDefault();
        if (station) {
          if (isCurrentStation) toggle();
          else play(station);
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, navigate, station, isCurrentStation, play, toggle, showQr]);

  if (!station) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center select-none">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6">
            <i className="ri-radio-line text-white/40 text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Emisora no encontrada</h1>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white">
            <i className="ri-arrow-left-line mr-2"></i>Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white select-none overflow-x-hidden">
      {/* Cover Background */}
      <div className="relative h-[380px] lg:h-[480px] overflow-hidden">
        <img src={station.logoUrl} alt="" className="w-full h-full object-cover opacity-30 blur-md scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/50 to-[#0a0a0f]/30"></div>

        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 lg:px-10 py-5 z-10">
          <button
            onClick={() => navigate("/tv-app")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"}`}
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Volver
          </button>
          <div className="flex items-center gap-3">
            {station.isLive && (
              <span className="px-3 py-1.5 rounded-full bg-red-500/90 text-white text-xs font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                EN VIVO
              </span>
            )}
            {isCurrentStation && isPlaying && (
              <span className="px-3 py-1.5 rounded-full bg-accent-400/90 text-white text-xs font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                SONANDO
              </span>
            )}
          </div>
        </div>

        {/* Station Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 z-10 px-6 lg:px-10 pb-8">
          <div className="flex items-end gap-6">
            <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-3xl overflow-hidden ring-4 ring-white/10 bg-[#0a0a0f] flex-shrink-0">
              <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl lg:text-4xl font-bold text-white">{station.name}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="text-lg text-white/70">{station.frequency}</span>
                <span className="text-sm text-white/40">{station.city}, {station.country}</span>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs">{station.category}</span>
              </div>
            </div>
            <button
              onClick={() => { if (isCurrentStation) toggle(); else play(station); }}
              className={`flex items-center gap-3 px-8 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-bold flex-shrink-0 ${focusedBtn === "play" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : isCurrentStation && isPlaying ? "bg-white text-[#0a0a0f] hover:bg-white/90" : "bg-white/80 text-[#0a0a0f] hover:bg-white"}`}
            >
              <i className={`text-xl ${isCurrentStation && isPlaying ? "ri-pause-fill" : "ri-play-fill"}`}></i>
              {isCurrentStation && isPlaying ? "Pausar" : "Reproducir"}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-10 py-8 space-y-10">
        {/* Stats Row */}
        <div className="flex items-center gap-10 flex-wrap">
          <div><p className="text-3xl font-bold text-white">{formatCount(station.listeners)}</p><p className="text-sm text-white/50 mt-1">Oyentes</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{station.rating}</p><p className="text-sm text-white/50 mt-1">Rating RFW</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-white">{profile?.foundedYear || "—"}</p><p className="text-sm text-white/50 mt-1">Fundada</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div className="flex items-center gap-2">
            {profile?.languages.map((l) => (
              <span key={l} className="px-3 py-1 rounded-full bg-white/5 text-white/60 text-xs font-semibold">{l}</span>
            ))}
          </div>
        </div>

        {/* Description */}
        {profile && (
          <div className="max-w-3xl">
            <p className="text-base text-white/60 leading-relaxed line-clamp-4">{profile.history}</p>
          </div>
        )}

        {/* Quick Links */}
        <div className="flex items-center gap-3 flex-wrap">
          {schedule.length > 0 && (
            <button tabIndex={0} onClick={() => scheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white">
              <i className="ri-calendar-schedule-line"></i>
              Programación
            </button>
          )}
          {stationClips.length > 0 && (
            <button tabIndex={0} onClick={() => videosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white">
              <i className="ri-film-line"></i>
              Videos ({stationClips.length})
            </button>
          )}
          {stationEvents.length > 0 && (
            <button tabIndex={0} onClick={() => eventsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white">
              <i className="ri-calendar-event-line"></i>
              Eventos ({stationEvents.length})
            </button>
          )}
          {stationContests.length > 0 && (
            <button tabIndex={0} onClick={() => contestsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white">
              <i className="ri-trophy-line"></i>
              Concursos ({stationContests.length})
            </button>
          )}
          <button onClick={() => setShowQr(true)} tabIndex={0} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white">
            <i className="ri-qr-code-line"></i>
            Abrir en el celu
          </button>
        </div>

        {/* Schedule */}
        {schedule.length > 0 && (
          <section ref={scheduleRef}>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><i className="ri-calendar-schedule-line text-emerald-400 text-sm"></i></div>
              Programación
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {schedule.map((s, i) => (
                <div key={i} tabIndex={0} data-tv-focusable="true" className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0"><i className="ri-time-line text-emerald-400"></i></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-white/60 font-semibold bg-white/5 px-2 py-0.5 rounded-md">{s.day}</span>
                      <span className="text-xs text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-md">{s.timeRange}</span>
                    </div>
                    <p className="text-base font-bold text-white">{s.program}</p>
                    <p className="text-sm text-white/50">{s.host}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Programs */}
        {stationPrograms.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><i className="ri-mic-line text-emerald-400 text-sm"></i></div>
              Programas ({stationPrograms.length})
            </h2>
            <TvCarousel>
              {stationPrograms.map((prog) => (
                <div
                  key={prog.id}
                  tabIndex={0}
                  onClick={() => navigate(`/tv-app/program/${prog.slug}`)}
                  data-tv-focusable="true"
                  className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-emerald-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-emerald-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
                >
                  <img src={prog.coverUrl} alt={prog.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>
                  {prog.isActive && (
                    <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-xs font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>ACTIVO
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-lg font-bold text-white truncate">{prog.name}</h3>
                    <p className="text-sm text-white/70 mt-1">{prog.hosts[0]?.name || "Varios"}</p>
                    <p className="text-xs text-white/50 mt-1">{prog.schedule}</p>
                  </div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        {/* Videos */}
        {stationClips.length > 0 && (
          <section ref={videosRef}>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent-400/10 flex items-center justify-center"><i className="ri-film-line text-accent-400 text-sm"></i></div>
              Videos ({stationClips.length})
            </h2>
            <TvCarousel>
              {stationClips.map((clip) => (
                <div
                  key={clip.id}
                  tabIndex={0}
                  onClick={() => navigate(`/tv-app/video/${clip.id}`)}
                  data-tv-focusable="true"
                  className="relative flex-shrink-0 w-[340px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-accent-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-accent-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
                >
                  <img src={clip.imagenUrl} alt={clip.titulo} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <i className="ri-play-fill text-white text-2xl ml-0.5"></i>
                    </div>
                  </div>
                  {clip.esTendencia && (
                    <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-rose-500/90 text-white text-xs font-bold">TENDENCIA</div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-lg font-bold text-white truncate">{clip.titulo}</h3>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-sm text-white/60 flex items-center gap-1"><i className="ri-eye-line text-xs"></i>{formatearNumero(clip.vistas)}</span>
                      <span className="text-sm text-white/60 flex items-center gap-1"><i className="ri-share-forward-line text-xs"></i>{formatearNumero(clip.compartidos)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        {/* Events */}
        {stationEvents.length > 0 && (
          <section ref={eventsRef}>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center"><i className="ri-calendar-event-line text-amber-400 text-sm"></i></div>
              Eventos ({stationEvents.length})
            </h2>
            <TvCarousel>
              {stationEvents.map((evt) => (
                <div
                  key={evt.id}
                  tabIndex={0}
                  onClick={() => navigate(`/tv-app/event/${evt.id}`)}
                  data-tv-focusable="true"
                  className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-amber-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-amber-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
                >
                  <img src={evt.image} alt={evt.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
                  <div className="absolute top-4 right-4"><span className="px-3 py-1 rounded-full bg-amber-500/90 text-white text-xs font-bold">{evt.type}</span></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-lg font-bold text-white truncate">{evt.title}</h3>
                    <p className="text-sm text-white/70 mt-1">{evt.location}</p>
                    <p className="text-xs text-white/50 mt-1 flex items-center gap-1.5"><i className="ri-calendar-line text-[11px]"></i>{evt.date} {evt.time}</p>
                  </div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        {/* Contests */}
        {stationContests.length > 0 && (
          <section ref={contestsRef}>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center"><i className="ri-trophy-line text-violet-400 text-sm"></i></div>
              Concursos ({stationContests.length})
            </h2>
            <TvCarousel>
              {stationContests.map((contest) => (
                <div
                  key={contest.id}
                  tabIndex={0}
                  onClick={() => navigate(`/tv-app/contest/${contest.id}`)}
                  data-tv-focusable="true"
                  className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-violet-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-violet-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 via-foreground-950/80 to-foreground-950/90"></div>
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/90 text-white text-xs font-bold">
                      <i className="ri-trophy-line text-xs"></i>
                      {contest.status === "activo" ? "ACTIVO" : contest.status === "proximamente" ? "PRÓXIMO" : "FINALIZADO"}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs font-bold truncate max-w-[140px]">{contest.prize.length > 20 ? contest.prize.slice(0, 20) + "…" : contest.prize}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-lg font-bold text-white truncate">{contest.title}</h3>
                    <p className="text-sm text-white/50 mt-1 truncate">{contest.description.split('.')[0]}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm text-white/70 flex items-center gap-1"><i className="ri-user-line text-xs"></i>{formatCount(contest.participants)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        {/* QR Handoff */}
        {showQr && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowQr(false)}>
            <div className="bg-[#0f0f15] border border-white/10 rounded-3xl p-8 max-w-md w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-48 h-48 mx-auto rounded-2xl bg-white p-3 mb-6">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://radiofrecuenciaweb.com/radio/${station.slug}`)}`} alt="QR" className="w-full h-full object-contain rounded-xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Abrir en el celular</h3>
              <p className="text-sm text-white/50 mb-2">Escaneá este código QR con tu celular para ver {station.name} en la app.</p>
              <p className="text-sm text-white/30 font-mono mb-6">radiofrecuenciaweb.com/radio/{station.slug}</p>
              <button onClick={() => setShowQr(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white">Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}